import { useContext, useEffect, useLayoutEffect, useMemo, useState, Fragment, useRef } from 'react';
import { RacingContext } from '../../../Home/RacingPage';
import { useTranslation } from 'react-i18next';
import { RsHorseName, RsJockey, RsTrainer } from '../../Common/RCOddsTableBase';
import Button from '../../../Common/ui-components/Button';
import OddsDropLegend from '../../Common/OddsDropLegend';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { useQuickBet } from '../../Common/QuickBetHook';
import PopUpLayer from '@/src/components/Common/ui-components/PopUpLayer';
import { useAdobeLaunch } from '@/src/components/Common/utils/adobeLaunch';
import { Switch } from '../../../Common/ui-components';
import { isStartSellpmPool } from '../../Common/Common';
import './result.scss';
import { useSearchParams } from 'react-router-dom';
import Sticky from 'react-stickynode';
import { scrollTo } from '@/src/components/Common/GlobalFunc';
import { useRaceSimUnityWebGLPopUp } from './UnityWebGLPopUp';
import { transformHorseAttributes, inputImportanceOrder } from '.';

const Title = ({ setLocalParaRef, rsResultShowMode }) => {
    const { t } = useTranslation();
    const [_, setSearchParams] = useSearchParams();

    return (
        <div className="rs-result-title">
            <span className="rs-result-title-text">{t('RS_SIMULATION_RESULT')}</span>
            <Button
                className="rs-result-title-button"
                themeColor="#122C68"
                variant={'outlined'}
                onClick={() => {
                    // resetKeyFactors()
                    setLocalParaRef.current({ rsShowPage: 'RS_KEY_FACTORS', filterValue: ['All'] });
                    setSearchParams((searchParams) => {
                        for (const key of searchParams.keys()) searchParams.delete(key);
                        searchParams.set('b_cid', `SPLDSP_eWinRacing_HorseSelector_Factor_${rsResultShowMode}`);
                        return searchParams;
                    });
                }}
            >
                {t('RS_SELECT_DIFFERENT_FACTORS')}
            </Button>
        </div>
    );
};

const Replay = ({
    result,
    resultVideoUrl,
    runnersData,
    isMobileSize,
    isLocal,
    raceNo,
    crossingLineImageUrl,
    showMore,
    rsResultShowMode
}) => {
    const { t } = useTranslation();

    const resultArr = result.split('/');
    let data = resultArr
        .map((no, index) => {
            let a = runnersData.find((i) => {
                return i.no === no;
            });
            if (a) {
                a.place = index + 1;
            }
            return a;
        })
        .filter(Boolean);
    data = data.splice(0, 4);

    const { open } = useRaceSimUnityWebGLPopUp();

    return (
        ((crossingLineImageUrl !== '' && !isMobileSize) || isMobileSize) && (
            <div
                className={`rs-result-replay ${isMobileSize ? 'rs-result-replay-mobile' : ''} ${
                    crossingLineImageUrl === '' ? 'rs-result-replay-notWatchVideo' : ''
                }  ${
                    isMobileSize
                        ? !showMore
                            ? 'rs-result-replay-mobile-showLess'
                            : 'rs-result-replay-mobile-showMore'
                        : ''
                }`}
            >
                {crossingLineImageUrl !== '' && (
                    <div className="rs-result-replay-img-con">
                        <div
                            className="rs-result-replay-button-con"
                            onClick={() => open(resultVideoUrl, rsResultShowMode)}
                        >
                            <span className="rs-result-replay-button"></span>
                            <span className="rs-result-replay-text">{t('RS_REPLAY')}</span>
                        </div>
                        {isMobileSize && !showMore ? (
                            <span
                                className="rs-result-replay-img-b"
                                style={{ backgroundImage: `URL(${crossingLineImageUrl})` }}
                            ></span>
                        ) : (
                            <>
                                {!isMobileSize && (
                                    <img className="rs-result-replay-img-back" src={crossingLineImageUrl}></img>
                                )}
                                <img className="rs-result-replay-img" src={crossingLineImageUrl}></img>
                            </>
                        )}
                    </div>
                )}
                {isMobileSize && !showMore && (
                    <div className="rs-result-replay-rank">
                        {data.map((item, k1) => {
                            return (
                                <div key={k1} className="rs-result-replay-rank-horse-list">
                                    <span
                                        className={`${
                                            item.place <= 4
                                                ? `rs-result-place-img rs-result-place-img${item.place}`
                                                : ''
                                        }`}
                                    >
                                        {item.place}
                                    </span>
                                    <span className="rs-result-replay-rank-horse">
                                        <span className="rs-result-replay-rank-horse-no">{item.no}.</span>
                                        <RsHorseName
                                            t={t}
                                            isLocal={isLocal}
                                            raceNo={raceNo}
                                            id={item.id}
                                            runnerNo={item.no}
                                            name={`${item.name}${item.isScratch ? ` (${t('LB_RC_SCR')})` : ''}`}
                                            racingColorFileName={item.racingColorFileName}
                                        />
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        )
    );
};

const oddSortArr = [
    { label: 'RS_All', value: 'All', min: -Infinity, max: Infinity },
    { label: 'RS_FAVOURITES', value: 'Favourites', min: 0, max: 5 },
    { label: 'RS_MID_PRICER', value: 'Mid-Pricer', min: 5, max: 15 },
    { label: 'RS_OUTSIDERS', value: 'Outsiders', min: 15, max: Infinity }
];

const Filter = ({
    result,
    runnersData,
    rsIsWatchVideo,
    isMobileSize,
    filterValue,
    setFilterValue,
    setLocalParaRef,
    resultStatus,
    isOpen,
    setIsOpen,
    rsResultShowMode
}) => {
    const { t, i18n } = useTranslation();
    const [isLargeImg, setIsLargeImg] = useState();
    // const [isOpen, setIsOpen] = useState(false);
    const onClose = () => {
        setIsOpen(false);
    };

    const [tmpFilterValue, setTmpFilterValue] = useState([...filterValue]);

    const { sendAdobeEvent } = useAdobeLaunch();
    const {
        content: { date, venue, raceNo }
    } = useContext(RacingContext);

    useEffect(() => {
        setTmpFilterValue([...filterValue]);
    }, [JSON.stringify(filterValue)]);

    const itemOnClick = (item) => {
        let newFilterValue = [...tmpFilterValue];
        if (item.value === 'All') {
            newFilterValue = ['All'];
        } else {
            const index = newFilterValue.findIndex((i) => i === 'All');
            index >= 0 && newFilterValue.splice(index, 1);
            if (!newFilterValue?.includes(item.value)) {
                newFilterValue.push(item.value);
            } else {
                const index = newFilterValue.findIndex((i) => i === item.value);
                index >= 0 && newFilterValue.splice(index, 1);
            }
            if (newFilterValue.length === 0) {
                newFilterValue = ['All'];
            }
        }
        setTmpFilterValue(newFilterValue);
    };

    if (['NOPOOLS', 'ABANDONED', 'VOID'].includes(resultStatus)) {
        return null;
    }

    return (
        <div className="rs-result-filter">
            <span
                className={`rs-result-filter-img ${
                    !tmpFilterValue?.includes('All') ? 'rs-result-filter-img-other' : ''
                }`}
            ></span>
            <PopUpLayer
                className="Win_Odds_Filter"
                size="large"
                onClose={() => {
                    onClose();
                    setTmpFilterValue([...filterValue]);
                }}
                title={t('RS_WIN_ODDS_FILTER')}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
            >
                <div style={{ minHeight: 100 }}>
                    {oddSortArr.map((item, index) => {
                        return (
                            <div
                                key={index}
                                onClick={() => {
                                    itemOnClick(item);
                                }}
                                className={`Win_Odds_Filter_item ${
                                    tmpFilterValue?.includes(item.value) ? 'Win_Odds_Filter_item_checked' : ''
                                }`}
                            >
                                {
                                    <span
                                        className={`Win_Odds_Filter_item_tick_img Win_Odds_Filter_item_tick_img_${
                                            !tmpFilterValue?.includes(item.value) ? 'hide' : ''
                                        }`}
                                    ></span>
                                }
                                <span className="Win_Odds_Filter_item_tick_text">{t(item.label)}</span>
                            </div>
                        );
                    })}
                </div>
                <Button
                    onClick={() => {
                        setIsOpen(false);
                        setLocalParaRef.current({ filterValue: tmpFilterValue });

                        sendAdobeEvent({
                            event: 'interaction',
                            interaction: { eventID: 'eWinRacing_HorseSelector-Result_ClickWinOddsFilter' },
                            content: {
                                category: rsResultShowMode,
                                race_no: `${date?.replaceAll('-', '')}-${venue}-${raceNo}`
                            },
                            user: {
                                preference: oddSortArr
                                    .map(({ value }) => `${value}:${tmpFilterValue.includes(value) ? 'Y' : 'N'}`)
                                    .join(',')
                            }
                        });
                    }}
                    themeColor="#122C68"
                    className="Win_Odds_Filter_button"
                >
                    {t('RS_CONFIRM')}
                </Button>
            </PopUpLayer>
        </div>
    );
};

const Last6 = ({ data }) => {
    return (
        <span className="Adjust-PopUpLayer-table-l6">
            {data.isScratch
                ? '--'
                : data?.last6?.split('/')?.map((i, index) => {
                      return (
                          <Fragment key={index}>
                              {index !== 0 && <span className={`Adjust-PopUpLayer-table-l6-arrow`}></span>}
                              <span className={`Adjust-PopUpLayer-table-l6-item Adjust-PopUpLayer-table-l6-${i}`}>
                                  {i}
                              </span>
                          </Fragment>
                      );
                  })}
        </span>
    );
};

const RankTable = ({
    result,
    runnersData,
    rsIsWatchVideo,
    isMobileSize,
    filterValue,
    setFilterValue,
    isLocal,
    setLocalParaRef,
    resultStatus,
    showFactors,
    setShowFactors,
    getDefaultHorseAttributes,
    rsKeyFactors,
    rsImportance,
    rsResultShowMode,
    resultVideoUrl
}) => {
    const { t, i18n } = useTranslation();
    const { rsResultSticky, setRsResultSticky } = useContext(GlobalContext);

    const context = useContext(RacingContext);

    const { venue, raceNo } = context.content;
    const mtg = context?.content?.meeting;

    let winPool = null;
    let plaPool = null;

    if (mtg.poolInvs != null) {
        winPool = mtg.poolInvs.filter((data) => data.leg.races[0] == raceNo && data.oddsType == 'WIN')[0];
        // plaPool = mtg.poolInvs.filter((data) => data.leg.races[0] == raceNo && data.oddsType == 'PLA')[0];
    }

    let winDisable = !isStartSellpmPool(winPool);
    // let plaDisable = !isStartSellpmPool(plaPool);
    const [isOpen, setIsOpen] = useState(false);
    // const [showFactors, setShowFactors] = useState(false);

    const { sendAdobeEvent } = useAdobeLaunch();

    const resultArr = result.split('/');
    let showData = useMemo(() => {
        let showData = [];
        const data = resultArr
            .map((no, index) => {
                let a = runnersData.find((i) => {
                    return i.no === no;
                });
                if (a) {
                    a.place = index + 1;
                }
                return a;
            })
            .filter(Boolean);
        if (filterValue?.includes('All')) {
            showData.push({
                tilte: '',
                data
            });
        } else {
            showData = oddSortArr
                .filter((i1) => filterValue?.includes(i1.value))
                .map((i2) => {
                    const data = runnersData.filter((i3) => {
                        return (
                            resultArr?.includes(i3.no) &&
                            parseFloat(i3?.winOdds?.odds || 0) > i2.min &&
                            parseFloat(i3?.winOdds?.odds || 0) <= i2.max
                        );
                    });
                    const sortData = data.sort((i4, i5) => {
                        if (parseFloat(i4?.winOdds?.odds || 0) !== parseFloat(i5?.winOdds?.odds || 0)) {
                            return parseFloat(i4?.winOdds?.odds || 0) - parseFloat(i5?.winOdds?.odds || 0);
                        } else {
                            return parseFloat(i4?.no) - parseFloat(i5?.no);
                        }
                    });
                    return {
                        tilte: i2.label,
                        data: sortData
                    };
                })
                .filter(Boolean);
        }
        return showData;
    }, [resultArr, runnersData, filterValue, oddSortArr]);

    const [enabledSticky, setEnabledSticky] = useState(true);
    useEffect(() => {
        if (isMobileSize) {
            const enabledStickyFn = () => {
                const suggestedEl = document.getElementById('rs-result-suggested-id');
                const rect2 = suggestedEl?.getBoundingClientRect();
                setEnabledSticky(isMobileSize && rect2?.top > 200);
            };

            window.addEventListener('scroll', enabledStickyFn);
            enabledStickyFn();
            return () => {
                window.removeEventListener('scroll', enabledStickyFn);
            };
        } else {
            setEnabledSticky(false);
        }
    }, []);

    const onStateChange = (status) => {
        if (status.status === Sticky.STATUS_FIXED) {
            setRsResultSticky(true);
        } else {
            setRsResultSticky(false);
        }
    };

    const { KeyFactorsOrder, ImportanceOrder } = window.RaceSimulatorConfig;
    const Glossary = window.RaceSimulatorConfig.Glossary[i18n.language];
    const urlPara = resultVideoUrl?.split('~');
    const p3Arr = urlPara?.[2]?.split('/');
    const keyStr = urlPara?.[0].slice(7, -1);
    const KeyFactorsArr = [];
    inputImportanceOrder.forEach((item, i) => {
        if (parseInt(keyStr.substring(i * 3, i * 3 + 3)) > 0) {
            KeyFactorsArr.push(item);
        }
    });

    return (
        <div className="rs-result-rank-table-con">
            <table className="rs-result-rank-table">
                <Sticky
                    onStateChange={onStateChange}
                    enabled={enabledSticky}
                    activeClass="rs-result-rank-table-Sticky-activeClass"
                    enableTransforms={false}
                    // bottomBoundary={'.rs-result-rank-table-con'}
                    top={72}
                >
                    <thead id="rs-result-rank-table-thead">
                        <tr className="show_result_title">
                            <th>{t('LB_RS_RESULT_OVERVIEW')}</th>
                            <th className="show_factors_con">
                                {t('LB_RS_SHOW_FACTORS')}
                                <Switch
                                    checked={showFactors}
                                    onChange={(v) => {
                                        setLocalParaRef.current({ showFactors: v });

                                        if (v) {
                                            sendAdobeEvent({
                                                event: 'interaction',
                                                interaction: {
                                                    eventID: 'eWinRacing_HorseSelector-Result_ClickShowFactor'
                                                },
                                                content: {
                                                    category: rsResultShowMode,
                                                    race_no: `${mtg?.date?.replaceAll('-', '')}-${venue}-${raceNo}`
                                                }
                                            });
                                        }
                                    }}
                                    checkedChildren={t('LB_RS_ON')}
                                    unCheckedChildren={t('LB_RS_OFF')}
                                />
                            </th>
                        </tr>
                        <tr className="rs-result-rank-table_title">
                            <th className="rs-result-place">{t('RS_PLACE')}</th>
                            <th className="rs-result-rank-table-no">{t('RS_NO')}</th>
                            <th className="rs-result-rank-table-horse">{t('RS_HORSE')}</th>
                            <th className="rs-result-rank-table-win-odds" onClick={() => setIsOpen(true)}>
                                {t('RS_WIN_ODDS')}
                                <Filter
                                    {...{
                                        filterValue,
                                        setFilterValue,
                                        setLocalParaRef,
                                        resultStatus,
                                        isOpen,
                                        setIsOpen,
                                        rsResultShowMode
                                    }}
                                />
                            </th>
                        </tr>
                    </thead>
                </Sticky>
                <tbody>
                    {showData.map((i1, index) => {
                        return (
                            <Fragment key={index}>
                                {i1.tilte && (
                                    <div className="rs-result-rank-table-title-con">
                                        <div className="rs-result-rank-table-title">{t(i1.tilte)}</div>
                                    </div>
                                )}
                                {i1?.data && i1?.data?.length > 0 ? (
                                    i1.data.map((data, index) => {
                                        let enableWinQuickBet =
                                            !data.selDisable && !winDisable && data.winOdds.odds != '';
                                        if (data) {
                                            const values = transformHorseAttributes(p3Arr[data.no - 1]);
                                            const showFactorsArr = [];
                                            let hasL6 = null;
                                            if (rsResultShowMode === 'Advanced') {
                                                ImportanceOrder.forEach((item) => {
                                                    if (KeyFactorsArr.includes(item)) {
                                                        const value = values?.[item];

                                                        if (item === 'l6' && window.innerWidth < 1480) {
                                                            hasL6 = {
                                                                key: item,
                                                                title: Glossary[item]['title'],
                                                                value
                                                            };
                                                            return;
                                                        }
                                                        showFactorsArr.push({
                                                            key: item,
                                                            title: Glossary[item]['title'],
                                                            value:
                                                                item === 'td'
                                                                    ? value?.join('-')
                                                                    : item === 'dw'
                                                                      ? data.draw
                                                                      : item === 'hr'
                                                                        ? parseInt(value)
                                                                        : value
                                                        });
                                                    }
                                                });
                                            } else {
                                                const defaultValue = getDefaultHorseAttributes(data.no - 1);
                                                KeyFactorsOrder.forEach((item) => {
                                                    if (KeyFactorsArr.includes(item)) {
                                                        const value = defaultValue?.[item];
                                                        showFactorsArr.push({
                                                            key: item,
                                                            title: Glossary[item]['title'],
                                                            value:
                                                                item === 'td'
                                                                    ? value?.join('-')
                                                                    : item === 'dw'
                                                                      ? data.draw
                                                                      : item === 'hr'
                                                                        ? parseInt(value)
                                                                        : value
                                                        });
                                                    }
                                                });
                                            }

                                            return (
                                                <div key={index} className="rs-result-rank-table-tr-con">
                                                    <tr>
                                                        <td className={`rs-result-place`}>
                                                            <span
                                                                className={`${
                                                                    data.place <= 4
                                                                        ? `rs-result-place-img rs-result-place-img${data.place}`
                                                                        : ''
                                                                }`}
                                                            >
                                                                {data.place}
                                                            </span>
                                                        </td>
                                                        <td className="rs-result-rank-table-no">
                                                            <span>{data.no}</span>
                                                            {data.colorTxt}
                                                        </td>
                                                        <td className="rs-result-rank-table-horse">
                                                            <div className="rs-result-rank-table-horse-container">
                                                                <div className="rs-result-rank-table-horse-content">
                                                                    <div className="rs-result-rank-table-horse-name">
                                                                        <RsHorseName
                                                                            t={t}
                                                                            isLocal={isLocal}
                                                                            raceNo={raceNo}
                                                                            id={data.id}
                                                                            runnerNo={data.no}
                                                                            name={`${data.name}${
                                                                                data.isScratch
                                                                                    ? ` (${t('LB_RC_SCR')})`
                                                                                    : ''
                                                                            }`}
                                                                            racingColorFileName={
                                                                                data.racingColorFileName
                                                                            }
                                                                        />
                                                                    </div>
                                                                    <div className="rs-result-rank-table-horse-JT">
                                                                        <div className="rs-result-rank-table-horse-J">
                                                                            <span className="rs-result-rank-table-horse-J-icon">
                                                                                {t('LB_RACE_JKC_SHORT')}
                                                                            </span>
                                                                            <span className="rs-result-rank-table-horse-J-name">
                                                                                {data.isScratch ? (
                                                                                    '--'
                                                                                ) : (
                                                                                    <RsJockey
                                                                                        t={t}
                                                                                        isLocal={isLocal}
                                                                                        raceNo={raceNo}
                                                                                        runnerNo={data.no}
                                                                                        jockyId={data.jockyId}
                                                                                        jocky={data.jocky}
                                                                                        alw={data.alw}
                                                                                    />
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                        <div className="rs-result-rank-table-horse-T">
                                                                            <span className="rs-result-rank-table-horse-T-icon">
                                                                                {t('LB_RACE_TRN_SHORT')}
                                                                            </span>
                                                                            <span className="rs-result-rank-table-horse-T-name">
                                                                                {data.isScratch ? (
                                                                                    '--'
                                                                                ) : (
                                                                                    <RsTrainer
                                                                                        t={t}
                                                                                        isLocal={isLocal}
                                                                                        raceNo={raceNo}
                                                                                        runnerNo={data.no}
                                                                                        trnId={data.trnId}
                                                                                        trn={data.trn}
                                                                                    />
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="rs-result-rank-table-win-odds">
                                                            <Odds
                                                                {...{
                                                                    enableQuickBet: enableWinQuickBet,
                                                                    data,
                                                                    betType: 'WIN',
                                                                    raceNo,
                                                                    venue,
                                                                    meetingDate: mtg?.date,
                                                                    odds: data?.winOdds?.odds,
                                                                    className: data?.winOdds?.className,
                                                                    rsResultShowMode
                                                                }}
                                                            />
                                                        </td>
                                                    </tr>
                                                    {showFactors && (
                                                        <div className="rs-result-rank-table-showFactors">
                                                            <div
                                                                className={`rs-result-rank-table-showFactors-other ${
                                                                    hasL6
                                                                        ? 'rs-result-rank-table-showFactors-hasL6'
                                                                        : ''
                                                                }`}
                                                            >
                                                                {showFactorsArr.map((item) => {
                                                                    return (
                                                                        <div className="rs-result-rank-table-showFactors-item">
                                                                            <span>{item.title}</span>
                                                                            {item.key === 'l6' ? (
                                                                                <Last6 data={data} />
                                                                            ) : (
                                                                                <span className="rs-result-rank-table-showFactors-item-value">
                                                                                    {data.isScratch ? '--' : item.value}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                            {hasL6 && (
                                                                <div className="rs-result-rank-table-showFactors-l6 rs-result-rank-table-showFactors-item">
                                                                    <span>{hasL6.title}</span>
                                                                    <Last6 data={data} />
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        }
                                        return null;
                                    })
                                ) : !filterValue?.includes('All') ? (
                                    <div className="rs-result-rank-table-No-Data-Found">
                                        {runnersData?.[0]?.['winOdds']?.['odds'] === '' ? null : t('RS_NO_DATA_FOUND')}
                                    </div>
                                ) : null}
                            </Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

const Odds = ({
    enableQuickBet,
    data,
    betType,
    raceNo,
    venue,
    meetingDate,
    odds,
    className = '',
    rsResultShowMode
}) => {
    const { t, i18n } = useTranslation();
    const { handleClickCombOdds } = useQuickBet();
    const { sendAdobeEvent } = useAdobeLaunch();

    if (!odds) {
        return null;
    }

    if (odds === t('LB_RC_SCR')) {
        return <div className="LB_RC_SCR">{t('LB_RC_SCR')}</div>;
    }

    return (
        <span
            onClick={() => {
                if (enableQuickBet) {
                    handleClickCombOdds(data, betType, raceNo, { type: 'raceSim', mode: rsResultShowMode });
                    console.debug(`${meetingDate}-${venue}-${raceNo}`);
                    sendAdobeEvent({
                        event: 'interaction',
                        interaction: {
                            eventID: 'eWinRacing_HorseSelector-Result_AddToBetslip'
                        },
                        content: {
                            category: rsResultShowMode,
                            race_no: `${meetingDate?.replaceAll('-', '')}-${venue}-${raceNo}`,
                            betType
                        }
                    });
                }
            }}
            className={`${['QTT', 'TCE'].includes(betType) ? 'rs-result-odds-add' : ''} ${className} rs-result-odds ${
                enableQuickBet ? 'enableQuickBet' : 'rs-result-odds-disable'
            }`}
        >
            {!['QTT', 'TCE'].includes(betType) ? odds : t('LB_ADDTOSLIP')}
        </span>
    );
};

const suggestedBetType = [
    {
        betType: 'WIN',
        Betline: [[1]],
        order: 'horse'
    },
    {
        betType: 'PLA',
        Betline: [[1], [2], [3]],
        order: 'horse'
    },
    {
        betType: 'QIN',
        Betline: [[1, 2]],
        order: 'horse'
    },
    {
        betType: 'QPL',
        Betline: [
            [1, 2],
            [1, 3],
            [2, 3]
        ],
        order: 'horse'
    },
    {
        betType: 'FCT',
        Betline: [[1, 2]],
        order: 'position'
    },
    {
        betType: 'TCE',
        Betline: [[1, 2, 3]],
        order: 'position'
    },
    {
        betType: 'TRI',
        Betline: [[1, 2, 3]],
        order: 'horse'
    },
    {
        betType: 'FF',
        Betline: [[1, 2, 3, 4]],
        order: 'horse'
    },
    {
        betType: 'QTT',
        Betline: [[1, 2, 3, 4]],
        order: 'position'
    }
];
const Suggested = ({
    result,
    runnersData,
    rsIsWatchVideo,
    isMobileSize,
    isLocal,
    filterValue,
    setFilterValue,
    setLocalParaRef,
    resultStatus,
    rsResultShowMode
}) => {
    const { t, i18n } = useTranslation();
    const resultArr = result.split('/');

    const context = useContext(RacingContext);

    const { venue, raceNo } = context.content;
    const mtg = context?.content?.meeting;
    let preSuffix = context.content.isPresales ? 'Pre' : '';

    if (mtg.poolInvs != null) {
        suggestedBetType.forEach((item) => {
            const pool = mtg.poolInvs.find((data) => data.leg.races[0] == raceNo && data.oddsType == item.betType);
            if (pool) {
                item.disablePool = !isStartSellpmPool(pool);
            } else {
                item.disablePool = true;
            }
        });
    }

    const reFreshOdds = () => {
        suggestedBetType.forEach((item) => {
            delete item.noPmPool;
            item.oddsArr = [];
            item.Betline.forEach((i1, index1) => {
                let rData = i1
                    .map((i2) => {
                        const horseNo = resultArr[i2 - 1];
                        return runnersData.find((r) => {
                            return r.no === horseNo;
                        });
                    })
                    .filter(Boolean);

                if (rData.length > 1 && item.order === 'horse') {
                    rData = rData.sort((i, j) => {
                        return i.no - j.no;
                    });
                }
                const horseNoArr = rData.map((data) => {
                    return String(data.no).padStart(2, '0');
                });
                const pmPool = mtg.pmPools.find(
                    (data) => data.leg.races[0] == raceNo && data.oddsType == item.betType + preSuffix
                );
                const hasRaceNopoolInvs = mtg.poolInvs.find((data) => data.leg.races[0] == raceNo);
                const poolInvs = mtg.poolInvs.find(
                    (data) => data.leg.races[0] == raceNo && data.oddsType == item.betType
                );
                const oddsObj = {};

                const isRefund = poolInvs?.status?.toUpperCase().indexOf('REFUND') >= 0;
                let isScratch = false;
                let isReserve = false;
                rData.forEach((item, index) => {
                    let runnerStat = item?.st?.toUpperCase();
                    if (runnerStat?.indexOf('SCRATCH') >= 0) {
                        isScratch = true;
                    } else if (['RESERVED', 'STANDBY'].includes(runnerStat)) {
                        isReserve = true;
                    }
                });

                const odds = pmPool?.oddsNodes?.find((item) => {
                    return item.combString === horseNoArr.join(',');
                });
                if (hasRaceNopoolInvs && !poolInvs) {
                    item.noPmPool = true;
                } else if (resultStatus === 'NOPOOLS') {
                    oddsObj.odds = '---';
                } else if (isRefund || isReserve) {
                    oddsObj.odds = '---';
                } else if (isScratch) {
                    oddsObj.odds = t('LB_RC_SCR');
                } else if (odds) {
                    if (odds.oddsValue == 'SCR') {
                        oddsObj.odds = t('LB_RC_SCR');
                    } else {
                        oddsObj.combString = odds.combString;
                        oddsObj.odds = odds.oddsValue;
                        oddsObj.hf = odds.hotFavourite;
                        oddsObj.od = odds.oddsDropValue;
                    }
                    if (oddsObj.hf && !isNaN(Number(oddsObj.odds))) {
                        oddsObj.className = 'favourite';
                    } else {
                        const isPresales = context?.content?.isPresales;
                        oddsObj.className = isPresales
                            ? ''
                            : oddsObj.od >= 50
                              ? 'od-50'
                              : oddsObj.od >= 20
                                ? 'od-20'
                                : '';
                    }
                } else if (['QTT', 'TCE'].includes(item.betType)) {
                    oddsObj.combString = horseNoArr.join(',');
                    oddsObj.odds = 'add';
                } else {
                    oddsObj.odds = '';
                }
                item.oddsArr.push(oddsObj);
            });
        });
    };
    useLayoutEffect(() => {
        reFreshOdds();
    });

    return (
        <div className="rs-result-suggested" id="rs-result-suggested-id">
            <div className="rs-result-suggested-title">{t('RS_SUGGESTED_BETLINE')}</div>
            <div className="rs-result-suggested-content">
                {suggestedBetType.map((item, k1) => {
                    if (item.noPmPool) {
                        return null;
                    }
                    return (
                        <div key={k1} className="rs-result-suggested-content-item">
                            <span className="rs-result-suggested-content-item-betType">{t(`LB_${item.betType}`)}</span>
                            <div className="rs-result-suggested-content-item-header">
                                <span>{t('RS_HORSE')}</span>
                                <span>{['QTT', 'TCE'].includes(item.betType) ? '' : t('RS_ODDS')}</span>
                            </div>
                            {item.Betline.map((i1, index1) => {
                                let data;
                                let rData = i1
                                    .map((i2) => {
                                        const horseNo = resultArr[i2 - 1];
                                        return runnersData.find((r) => {
                                            return r.no === horseNo;
                                        });
                                    })
                                    .filter(Boolean);

                                if (rData.length > 1 && item.order === 'horse') {
                                    rData = rData.sort((i, j) => {
                                        return i.no - j.no;
                                    });
                                }
                                if (['WIN', 'PLA'].includes(item.betType)) {
                                    data = rData[0];
                                } else {
                                    data = item?.oddsArr?.[index1];
                                }
                                return (
                                    <div key={index1} className="rs-result-suggested-content-item-con">
                                        <div className="rs-result-suggested-content-item-con-horse-c">
                                            {rData.map((data) => {
                                                return (
                                                    <span className="rs-result-suggested-content-item-con-horse">
                                                        <span className="rs-result-suggested-content-item-con-horse-no">
                                                            {data.no}.&nbsp;
                                                        </span>
                                                        <RsHorseName
                                                            t={t}
                                                            isLocal={isLocal}
                                                            raceNo={raceNo}
                                                            id={data.id}
                                                            runnerNo={data.no}
                                                            name={`${data.name}${
                                                                data.isScratch ? ` (${t('LB_RC_SCR')})` : ''
                                                            }`}
                                                            racingColorFileName={data.racingColorFileName}
                                                        />
                                                    </span>
                                                );
                                            })}
                                        </div>
                                        <Odds
                                            {...{
                                                enableQuickBet:
                                                    !item.selDisable &&
                                                    !item.disablePool &&
                                                    item?.oddsArr?.[index1]?.odds != '' &&
                                                    item?.oddsArr?.[index1]?.odds != '---' &&
                                                    item?.oddsArr?.[index1]?.odds != t('LB_RC_SCR'),
                                                data,
                                                betType: item.betType,
                                                raceNo,
                                                venue,
                                                meetingDate: mtg?.date,
                                                odds: item?.oddsArr?.[index1]?.odds,
                                                className: item?.oddsArr?.[index1]?.className,
                                                rsResultShowMode
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const ShowMore = ({ showMore, setShowMore, setLocalParaRef }) => {
    const { rsResultSticky, setRsResultSticky } = useContext(GlobalContext);
    const { t, i18n } = useTranslation();
    return (
        <div
            className={`rs-result-show-more ${!showMore ? 'rs-result-show-less' : ''}`}
            onClick={() => {
                document.documentElement.scrollTop = 0;
                scrollTo(0, 0);
                // setTimeout(() => {
                //     document.documentElement.scrollTop = 0
                // }, 100)
                setRsResultSticky(false);
                setLocalParaRef.current({ showMore: !showMore, filterValue: ['All'] });
            }}
        >
            {!showMore ? <span>{t('RS_SHOW_MORE')}</span> : <span>{t('RS_SHOW_LESS')}</span>}
            <span className={`rs-result-show-more-arrow ${showMore ? 'rs-result-show-less-arrow' : ''}`}></span>
        </div>
    );
};

const Result = ({
    result,
    resultVideoUrl,
    isMobileSize,
    runnersData,
    isLocal,
    raceNo,
    filterValue,
    setFilterValue,
    crossingLineImageUrl,
    showMore,
    setShowMore,
    resultStatus,
    showFactors,
    setShowFactors,
    getDefaultHorseAttributes,
    rsKeyFactors,
    rsImportance,
    rsResultShowMode
}) => {
    const { t } = useTranslation();
    const { setLocalParaRef, rsIsWatchVideo } = useContext(GlobalContext);

    return (
        <>
            <div className="rs-result">
                <div className="rs-result-left">
                    <Title setLocalParaRef={setLocalParaRef} rsResultShowMode={rsResultShowMode} />
                    <div className="rs-result-left-top">
                        {
                            <Replay
                                {...{
                                    result,
                                    resultVideoUrl,
                                    runnersData,
                                    isMobileSize,
                                    isLocal,
                                    raceNo,
                                    crossingLineImageUrl,
                                    showMore,
                                    rsResultShowMode
                                }}
                            />
                        }
                        {(!isMobileSize || (isMobileSize && showMore === true)) && (
                            <div className="rs-result-rank">
                                <RankTable
                                    {...{
                                        result,
                                        runnersData,
                                        rsIsWatchVideo,
                                        isMobileSize,
                                        filterValue,
                                        setFilterValue,
                                        isLocal,
                                        raceNo,
                                        setLocalParaRef,
                                        resultStatus,
                                        showFactors,
                                        setShowFactors,
                                        getDefaultHorseAttributes,
                                        rsKeyFactors,
                                        rsImportance,
                                        rsResultShowMode,
                                        resultVideoUrl
                                    }}
                                />
                            </div>
                        )}
                        {isMobileSize && (
                            <ShowMore
                                {...{
                                    showMore,
                                    setShowMore,
                                    setLocalParaRef
                                }}
                            />
                        )}
                    </div>
                    {isMobileSize == false && <OddsDropLegend />}
                    {isMobileSize == false && (
                        <div className="RS_REFERENCE">
                            <div>{t('TNC_RG_MESSAGE1')}</div>
                            <div>{t('TNC_RG_MESSAGE2')}</div>
                            <div>{t('TNC_RG_MESSAGE3')}</div>
                        </div>
                    )}
                </div>
                <div className="rs-result-right">
                    <Suggested
                        {...{
                            result,
                            runnersData,
                            rsIsWatchVideo,
                            isMobileSize,
                            isLocal,
                            raceNo,
                            filterValue,
                            setFilterValue,
                            setLocalParaRef,
                            resultStatus,
                            rsResultShowMode
                        }}
                    />
                </div>
                {isMobileSize && <OddsDropLegend />}
                {isMobileSize && (
                    <div className="RS_REFERENCE">
                        <div>{t('TNC_RG_MESSAGE1')}</div>
                        <div>{t('TNC_RG_MESSAGE2')}</div>
                        <div>{t('TNC_RG_MESSAGE3')}</div>
                    </div>
                )}
            </div>
        </>
    );
};
export default Result;
